// Client for the "act" Edge Function: every write a person makes from these screens (docs/SECURITY.md).
// Signed-in users can only read the database directly. Writes are POSTed here with the session's access token; the
// function checks the user, forces their business and 'human:<their email>' as the actor, and calls the crm_* function.
import type { SupabaseClient } from "@supabase/supabase-js";
import { env, functionUrl } from "./env";
import { httpFetch } from "./http";

export type ActFn = (action: string, params: Record<string, unknown>) => Promise<unknown>;

export interface ActOptions {
  url?: string;
  anonKey?: string;
  /** Defaults to httpFetch, which demo mode swaps for its in-memory stand-in. */
  fetchImpl?: typeof fetch;
}

export function makeAct(sb: SupabaseClient, opts: ActOptions = {}): ActFn {
  return async (action, params) => {
    const { data } = await sb.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Your session has ended. Sign in again.");
    const f = opts.fetchImpl ?? httpFetch;
    let res: Response;
    try {
      res = await f(opts.url ?? functionUrl("act"), {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
        body: JSON.stringify({ action, params }),
      });
    } catch {
      throw new Error("Can't reach the server. Check your connection and try again.");
    }
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; result?: unknown; error?: string };
    if (res.status === 404 && body.ok === undefined) {
      throw new Error("Saving changes isn't switched on yet. Try again later; if it keeps happening, contact Ogma's team.");
    }
    if (!res.ok || body.ok !== true) throw new Error(body.error ?? "Something went wrong. Try again in a moment.");
    return body.result ?? null;
  };
}
